import { Link } from 'react-router-dom';
import { Mail, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import type { Academy } from '@court-sync/shared';

export interface StarterPortalUnavailableProps {
  academy: Academy;
  portalLabel: 'Student & guardian portal' | 'Coach portal';
  /** Optional path to public lessons browse (contact-only on Starter). */
  publicLessonsPath?: string;
}

/**
 * Shown when an academy is on Starter and member/coach dashboards are not available.
 */
export function StarterPortalUnavailable({
  academy,
  portalLabel,
  publicLessonsPath,
}: StarterPortalUnavailableProps) {
  const contactEmail = academy.email?.trim();
  const contactPhone = academy.phone?.trim();

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col items-center px-4 py-16 text-center">
      <DashboardEmptyStateCard className="w-full">
        <DashboardEmptyStateIcon icon={Sparkles} />
        <h1 className="mb-2 text-xl font-semibold text-foreground">
          {portalLabel} not available on this plan
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{academy.name}</span> uses the Starter plan.
          Online self-service booking and member dashboards are included on Professional and above.
          Browse the public lesson catalogue and submit a booking request, or contact the academy directly.
        </p>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center">
          {publicLessonsPath ? (
            <Button asChild variant="default">
              <Link to={publicLessonsPath}>Browse lessons</Link>
            </Button>
          ) : null}
          {contactEmail ? (
            <Button asChild variant={publicLessonsPath ? 'outline' : 'default'}>
              <a href={`mailto:${contactEmail}`}>
                <Mail className="mr-2 h-4 w-4" aria-hidden />
                Email academy
              </a>
            </Button>
          ) : null}
        </div>
        {contactPhone ? (
          <p className="mt-4 text-sm text-muted-foreground">
            Phone:{' '}
            <a href={`tel:${contactPhone}`} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
              {contactPhone}
            </a>
          </p>
        ) : null}
      </DashboardEmptyStateCard>
    </div>
  );
}
